/**
 * LUXIRE COLLECTION FILTER ENGINE v2
 * ──────────────────────────────────────────────────────────────
 * Tag-based product filtering for Luxire collection pages.
 *
 * Architecture:
 *   1. First-page products parsed from DOM (instant)
 *   2. Full collection loaded via JSON API in background
 *   3. Filters built dynamically from luxire: tags on products
 *   4. Faceted counts update per Amazon/Uniqlo pattern
 *
 * Tag format:   luxire:category:value  (e.g. luxire:color:navy)
 * Filter logic: OR within category, AND across categories
 * URL state:    ?f.color=navy,blue&f.weave=oxford&price=100-500
 *
 * @version 2.1.0
 */
(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════
     CONFIGURATION
     Pure presentation data — labels, hex colors, display order.
     Actual product data comes from luxire: tags.
     ═══════════════════════════════════════════════════════════════ */

  const FILTER_CONFIG = {
    color: {
      label: 'Color',
      type: 'color',
      order: [
        'white', 'black', 'grey', 'navy', 'blue', 'brown',
        'green', 'red', 'pink', 'purple', 'yellow', 'orange',
        'beige', 'burgundy'
      ],
      display: {
        white:    { label: 'White / Cream',   hex: '#f5f5f0', border: true },
        black:    { label: 'Black',           hex: '#1a1a1a' },
        grey:     { label: 'Grey',            hex: '#6b7280' },
        navy:     { label: 'Navy',            hex: '#1e3a5f' },
        blue:     { label: 'Blue',            hex: '#3b82f6' },
        brown:    { label: 'Brown / Tan',     hex: '#92400e' },
        green:    { label: 'Green',           hex: '#166534' },
        red:      { label: 'Red',             hex: '#b91c1c' },
        pink:     { label: 'Pink',            hex: '#ec4899' },
        purple:   { label: 'Purple',          hex: '#7c3aed' },
        yellow:   { label: 'Yellow / Gold',   hex: '#ca8a04' },
        orange:   { label: 'Orange',          hex: '#ea580c' },
        beige:    { label: 'Beige / Khaki',   hex: '#d4b896' },
        burgundy: { label: 'Burgundy / Wine', hex: '#722F37' }
      },
      /** Map classifier's granular color tags to display swatches */
      groups: {
        white:    { tags: ['white', 'off-white'] },
        black:    { tags: ['black'] },
        grey:     { tags: ['grey', 'charcoal'] },
        navy:     { tags: ['navy'] },
        blue:     { tags: ['blue', 'light-blue', 'royal-blue', 'teal'] },
        brown:    { tags: ['brown', 'tan'] },
        green:    { tags: ['green', 'olive'] },
        red:      { tags: ['red'] },
        pink:     { tags: ['pink'] },
        purple:   { tags: ['purple'] },
        yellow:   { tags: ['yellow'] },
        orange:   { tags: ['orange'] },
        beige:    { tags: ['khaki'] },
        burgundy: { tags: ['burgundy'] }
      }
    },

    'primary-fiber': {
      label: 'Fabric',
      type: 'checkbox',
      display: {
        cotton:    { label: 'Cotton' },
        wool:      { label: 'Wool' },
        linen:     { label: 'Linen' },
        silk:      { label: 'Silk' },
        cashmere:  { label: 'Cashmere' },
        polyester: { label: 'Polyester' },
        nylon:     { label: 'Nylon' },
        lycra:     { label: 'Lycra / Stretch' },
        viscose:   { label: 'Viscose' },
        tencel:    { label: 'Tencel' }
      }
    },

    season: {
      label: 'Season',
      type: 'checkbox',
      order: ['summer', 'winter', 'year-round'],
      display: {
        summer:       { label: 'Summer' },
        winter:       { label: 'Winter' },
        'year-round': { label: 'Year-Round' }
      }
    },

    'weight-class': {
      label: 'Weight',
      type: 'checkbox',
      order: ['ultralight', 'lightweight', 'medium', 'heavyweight', 'super-heavy'],
      display: {
        ultralight:    { label: 'Ultralight (under 130 gsm)' },
        lightweight:   { label: 'Lightweight (130–200 gsm)' },
        medium:        { label: 'Medium (200–300 gsm)' },
        heavyweight:   { label: 'Heavyweight (300–400 gsm)' },
        'super-heavy': { label: 'Super Heavy (400+ gsm)' }
      }
    },

    weave: {
      label: 'Weave',
      type: 'checkbox',
      display: {
        oxford:       { label: 'Oxford' },
        poplin:       { label: 'Poplin' },
        twill:        { label: 'Twill' },
        flannel:      { label: 'Flannel' },
        chambray:     { label: 'Chambray' },
        herringbone:  { label: 'Herringbone' },
        corduroy:     { label: 'Corduroy' },
        denim:        { label: 'Denim' },
        canvas:       { label: 'Canvas' },
        pinpoint:     { label: 'Pinpoint' },
        broadcloth:   { label: 'Broadcloth' },
        dobby:        { label: 'Dobby' },
        hopsack:      { label: 'Hopsack' },
        birdseye:     { label: "Bird's Eye" },
        gabardine:    { label: 'Gabardine' },
        seersucker:   { label: 'Seersucker' },
        'end-on-end': { label: 'End-on-End' },
        jacquard:     { label: 'Jacquard' },
        plain:        { label: 'Plain Weave' },
        tropical:     { label: 'Tropical' }
      }
    },

    pattern: {
      label: 'Pattern',
      type: 'checkbox',
      /**
       * Groups consolidate granular tag values into clean filter options.
       * Clicking "Stripes" matches bengal-stripe, pin-stripe, etc.
       * Granular tags remain on products for the recommendation engine.
       */
      groups: {
        solid:       { label: 'Solid',           tags: ['solid'] },
        stripes:     { label: 'Stripes',         tags: ['stripes', 'bengal-stripe', 'pin-stripe', 'pencil-stripe', 'candy-stripe', 'chalk-stripe', 'university-stripe'] },
        checks:      { label: 'Checks',          tags: ['checks', 'gingham', 'tattersall', 'glen-check', 'windowpane', 'graph-check', 'gun-club', 'houndstooth'] },
        plaid:       { label: 'Plaid / Tartan',   tags: ['plaid', 'tartan', 'madras'] },
        herringbone: { label: 'Herringbone',      tags: ['herringbone'] },
        textured:    { label: 'Textured',         tags: ['textured', 'birdseye'] },
        paisley:     { label: 'Paisley',          tags: ['paisley'] },
        floral:      { label: 'Floral',           tags: ['floral'] },
        geometric:   { label: 'Geometric',        tags: ['geometric'] }
      }
    },

    formality: {
      label: 'Formality',
      type: 'checkbox',
      order: ['1', '2', '3', '4', '5'],
      display: {
        '1': { label: '① Casual' },
        '2': { label: '② Smart Casual' },
        '3': { label: '③ Business Casual' },
        '4': { label: '④ Business' },
        '5': { label: '⑤ Formal' }
      }
    },

    mill: {
      label: 'Mill / Brand',
      type: 'checkbox',
      display: {
        'dugdale':          { label: 'Dugdale Bros' },
        'vbc':              { label: 'Vitale Barberis Canonico' },
        'brisbane-moss':    { label: 'Brisbane Moss' },
        'thomas-mason':     { label: 'Thomas Mason' },
        'albini':           { label: 'Albini' },
        'canclini':         { label: 'Canclini' },
        'grandi-rubinelli': { label: 'Grandi & Rubinelli' },
        'drago':            { label: 'Drago' },
        'brembana':         { label: 'Brembana' },
        'e-thomas':         { label: 'E. Thomas' },
        'huddersfield':     { label: 'Huddersfield' },
        'reda':             { label: 'Reda' },
        'zegna':            { label: 'Ermenegildo Zegna' },
        'loro-piana':       { label: 'Loro Piana' },
        'caccioppoli':      { label: 'Caccioppoli' },
        'solbiati':         { label: 'Solbiati' }
      }
    }
  };

  /** Filter group order per product type — determines sidebar layout */
  const TYPE_FILTERS = {
    pants:    ['color', 'primary-fiber', 'season', 'weight-class', 'weave', 'pattern', 'formality', 'mill'],
    shirt:    ['color', 'weave', 'pattern', 'formality', 'primary-fiber', 'season', 'mill'],
    jacket:   ['color', 'primary-fiber', 'pattern', 'season', 'formality', 'weave', 'mill'],
    suit:     ['color', 'primary-fiber', 'pattern', 'season', 'formality', 'weave', 'mill'],
    _default: ['color', 'primary-fiber', 'season', 'weight-class', 'weave', 'pattern', 'formality', 'mill']
  };

  /** Tags to skip when extracting attributes (internal/redundant) */
  const SKIP_CATEGORIES = new Set([
    'UNKNOWN', 'composition', 'weight-gsm', 'formality-fabric', 'formality-style'
  ]);


  /* ═══════════════════════════════════════════════════════════════
     UTILITIES
     Pure functions — no state, no DOM access.
     ═══════════════════════════════════════════════════════════════ */

  /**
   * Build reverse lookup maps for grouped filter categories.
   * Returns: { pattern: { 'bengal-stripe': 'stripes', 'pin-stripe': 'stripes', ... } }
   */
  function buildGroupMaps(config) {
    const maps = {};
    for (const [cat, cfg] of Object.entries(config)) {
      if (!cfg.groups) continue;
      maps[cat] = {};
      for (const [groupKey, group] of Object.entries(cfg.groups)) {
        for (const tag of group.tags) {
          maps[cat][tag] = groupKey;
        }
      }
    }
    return maps;
  }

  const GROUP_MAPS = buildGroupMaps(FILTER_CONFIG);

  /**
   * Resolve raw tag values to filter group keys.
   * Ungrouped categories pass through unchanged.
   * Example: resolveGroups('pattern', ['bengal-stripe', 'solid']) → ['stripes', 'solid']
   */
  function resolveGroups(category, rawValues) {
    const map = GROUP_MAPS[category];
    if (!map) return rawValues;
    const result = new Set();
    for (const v of rawValues) result.add(map[v] || v);
    return [...result];
  }

  /**
   * Extract luxire: tag attributes from a tags array.
   * Returns: { color: ['navy'], pattern: ['bengal-stripe'], ... }
   */
  function extractAttributes(tags) {
    const attrs = {};
    for (const tag of tags) {
      if (!tag.startsWith('luxire:')) continue;
      const rest = tag.slice(7); // 'luxire:'.length === 7
      const i = rest.indexOf(':');
      if (i === -1) continue;

      const category = rest.slice(0, i);
      if (SKIP_CATEGORIES.has(category)) continue;

      const value = rest.slice(i + 1);
      if (!attrs[category]) attrs[category] = [];
      if (!attrs[category].includes(value)) attrs[category].push(value);
    }
    return attrs;
  }

  /**
   * Test whether a product passes a set of active filters.
   * Shared by applyFilters() and updateFacetedCounts() — single source of truth.
   *
   * @param {Object}      product      - Product with .attributes, .price, .available
   * @param {Object}      filters      - { color: ['navy'], pattern: ['stripes'] }
   * @param {Object}      priceRange   - { min: 10000, max: 50000 } (cents, null = unbounded)
   * @param {boolean}     inStockOnly  - Filter to available products only
   * @param {string|null} skipCategory - Exclude this category (for faceted counts)
   */
  function matchesFilters(product, filters, priceRange, inStockOnly, skipCategory) {
    for (const cat in filters) {
      if (cat === skipCategory) continue;
      const selected = filters[cat];
      if (!selected.length) continue;
      const values = resolveGroups(cat, product.attributes[cat] || []);
      if (!selected.some(v => values.includes(v))) return false;
    }
    if (priceRange.min !== null && product.price < priceRange.min) return false;
    if (priceRange.max !== null && product.price > priceRange.max) return false;
    if (inStockOnly && !product.available) return false;
    return true;
  }

  /** Sort keys by defined order first, then by count descending. */
  function sortByOrderThenCount(keys, order, counts) {
    return keys.slice().sort((a, b) => {
      const ai = order.indexOf(a), bi = order.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return (counts[b] || 0) - (counts[a] || 0);
    });
  }

  /** Convert "bengal-stripe" → "Bengal Stripe" */
  function titleCase(str) {
    return str.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  /** Look up display label for a filter value, checking display then groups. */
  function getFilterLabel(category, value) {
    const cfg = FILTER_CONFIG[category];
    if (!cfg) return titleCase(value);
    return cfg.display?.[value]?.label || cfg.groups?.[value]?.label || titleCase(value);
  }

  /** Format cents as "$129.99 USD" */
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2) + ' USD';
  }

  /** Reusable SVG fragments */
  const SVG = {
    check:   '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>',
    chevron: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>',
    close:   '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
    image:   '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>'
  };

  /** Cached element for HTML-escaping text */
  const _escDiv = document.createElement('div');
  function escapeHTML(text) {
    _escDiv.textContent = text;
    return _escDiv.innerHTML;
  }


  /* ═══════════════════════════════════════════════════════════════
     PRODUCT PARSERS
     Convert DOM cards and JSON API responses into a common format.
     ═══════════════════════════════════════════════════════════════ */

  function parseProductCard(card) {
    const tags = (card.dataset.productTags || '').split(',').map(t => t.trim());
    return {
      element:      card,
      id:           card.dataset.productId,
      title:        card.dataset.productTitle || '',
      url:          card.querySelector('a')?.href || '',
      image:        card.querySelector('img')?.src || '',
      hoverImage:   card.querySelector('.lx-product-card__hover-img')?.src || '',
      vendor:       card.dataset.productVendor || '',
      price:        parseInt(card.dataset.productPrice) || 0,
      comparePrice: parseInt(card.dataset.productComparePrice) || 0,
      available:    card.dataset.productAvailable === 'true',
      tags:         tags,
      attributes:   extractAttributes(tags),
      inDom:        true
    };
  }

  function parseProductJSON(p) {
    const tags = typeof p.tags === 'string'
      ? p.tags.split(',').map(t => t.trim())
      : (p.tags || []);

    const v = (p.variants && p.variants[0]) || {};
    const price = Math.round(parseFloat(v.price || p.price || '0') * 100);
    const comparePrice = Math.round(parseFloat(v.compare_at_price || p.compare_at_price || '0') * 100);
    const images = p.images || [];

    return {
      element:      null,
      id:           String(p.id),
      title:        p.title || '',
      url:          '/products/' + p.handle,
      image:        images[0]?.src || '',
      hoverImage:   images[1]?.src || '',
      vendor:       p.vendor || '',
      price:        price,
      comparePrice: comparePrice,
      available:    p.available !== false && v.available !== false,
      tags:         tags,
      attributes:   extractAttributes(tags),
      inDom:        false
    };
  }


  /* ═══════════════════════════════════════════════════════════════
     DOM CACHE
     Cached references to frequently-accessed elements.
     ═══════════════════════════════════════════════════════════════ */

  let dom = {};

  function cacheDom() {
    dom = {
      grid:          document.getElementById('productGrid'),
      sidebar:       document.getElementById('filtersSidebar'),
      form:          document.getElementById('filtersForm'),
      priceMin:      document.getElementById('priceMin'),
      priceMax:      document.getElementById('priceMax'),
      inStockToggle: document.getElementById('inStockOnly'),
      visibleCount:  document.getElementById('visibleCount'),
      resultCount:   document.getElementById('resultCount'),
      totalCount:    document.getElementById('totalCount'),
      emptyState:    document.getElementById('emptyState'),
      activeSection: document.getElementById('activeFilters'),
      activeTags:    document.getElementById('activeFilterTags'),
      activeNum:     document.getElementById('activeFilterNum'),
      filterBadge:   document.getElementById('activeFilterCount'),
      toolbarBadge:  document.getElementById('toolbarBadge'),
      pagination:    document.querySelector('.lx-pagination'),
      sortSelect:    document.getElementById('sortSelect')
    };
  }


  /* ═══════════════════════════════════════════════════════════════
     FILTER ENGINE
     ═══════════════════════════════════════════════════════════════ */

  const Engine = {

    // ── State ──
    products: [],
    allProducts: [],
    activeFilters: {},
    priceRange: { min: null, max: null },
    inStockOnly: false,
    isFullyLoaded: false,
    gridReplaced: false,
    totalProducts: 0,
    collectionHandle: '',
    collectionType: null,


    /* ──────────────────────────────────────────────────────────
       INIT
       ────────────────────────────────────────────────────────── */

    init() {
      if (!document.getElementById('productGrid')) return;
      cacheDom();

      this.totalProducts = parseInt(dom.grid.dataset.totalProducts) || 0;
      this.collectionHandle = dom.grid.dataset.collectionHandle || '';

      this.parseVisibleProducts();
      this.detectCollectionType();
      this.buildFilterSidebar();
      this.restoreFromURL();
      this.buildFilterOptions();
      this.bindEvents();
      this.applyFilters();
      this.loadFullCollection();
    },


    /* ──────────────────────────────────────────────────────────
       PRODUCT PARSING
       ────────────────────────────────────────────────────────── */

    parseVisibleProducts() {
      const cards = dom.grid.querySelectorAll('.lx-product-card[data-product-id]');
      this.products = Array.from(cards).map(parseProductCard);
      this.allProducts = this.products.slice();
      dom.totalCount.textContent = this.totalProducts;
    },

    detectCollectionType() {
      const h = this.collectionHandle.toLowerCase();
      if (/pant|trouser|chino|jean|gurkha/.test(h)) return (this.collectionType = 'pants');
      if (/shirt|oxford|chambray|linen-shirt/.test(h)) return (this.collectionType = 'shirt');
      if (/jacket|blazer|sport-coat/.test(h)) return (this.collectionType = 'jacket');
      if (/suit/.test(h)) return (this.collectionType = 'suit');

      // Fallback: detect dominant type from first page
      const counts = {};
      for (const p of this.products) {
        for (const t of (p.attributes.type || [])) counts[t] = (counts[t] || 0) + 1;
      }
      const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
      if (top && top[1] > this.products.length * 0.6) this.collectionType = top[0];
    },


    /* ──────────────────────────────────────────────────────────
       FULL COLLECTION LOADER
       Fetches remaining products via Shopify JSON API.
       250 per request — 1,884 products in ~8 fast requests.
       ────────────────────────────────────────────────────────── */

    async loadFullCollection() {
      if (this.totalProducts <= 48) {
        this.isFullyLoaded = true;
        this.buildFilterOptions();
        return;
      }

      const loadedIds = new Set(this.allProducts.map(p => p.id));
      const maxPages = Math.ceil(this.totalProducts / 250);

      for (let page = 1; page <= maxPages; page++) {
        try {
          const url = `/collections/${this.collectionHandle}/products.json?limit=250&page=${page}`;
          const resp = await fetch(url);
          if (!resp.ok) {
            console.warn('[Luxire] Products API returned', resp.status, '— stopping pagination');
            break;
          }

          const products = (await resp.json()).products || [];
          for (const p of products) {
            const id = String(p.id);
            if (!loadedIds.has(id)) {
              this.allProducts.push(parseProductJSON(p));
              loadedIds.add(id);
            }
          }

          if (products.length < 250) break;
        } catch (err) {
          console.warn('[Luxire] Failed to load products page', page, err);
          break;
        }
      }

      this.isFullyLoaded = true;
      this.buildFilterOptions();
      this.applyFilters();
    },


    /* ──────────────────────────────────────────────────────────
       SIDEBAR CONSTRUCTION
       Builds filter groups dynamically based on collection type.
       ────────────────────────────────────────────────────────── */

    buildFilterSidebar() {
      if (!dom.form) return;
      const filterOrder = TYPE_FILTERS[this.collectionType] || TYPE_FILTERS._default;

      // Preserve static groups (price at top, availability at bottom)
      const staticGroups = dom.form.querySelectorAll('[data-filter-group]:not([data-filter-category])');
      const availGroup = staticGroups.length > 1 ? staticGroups[staticGroups.length - 1] : null;

      // Remove previous dynamic groups
      dom.form.querySelectorAll('[data-filter-category]').forEach(g => g.remove());

      for (const category of filterOrder) {
        const config = FILTER_CONFIG[category];
        if (!config) continue;

        const collapsed = category === 'mill' || category === 'formality';
        const listTag = config.type === 'color'
          ? `<div class="lx-filter-colors" data-filter-list="${category}"></div>`
          : `<ul class="lx-filter-options" data-filter-list="${category}"></ul>`;

        const group = document.createElement('div');
        group.className = 'lx-filter-group';
        group.setAttribute('data-filter-group', '');
        group.setAttribute('data-filter-category', category);
        const loadingBars = '<span class="lx-filter-loading" data-filter-loading>' +
            '<span class="lx-filter-loading__bar"></span>' +
            '<span class="lx-filter-loading__bar"></span>' +
            '<span class="lx-filter-loading__bar"></span>' +
            '<span class="lx-filter-loading__bar"></span></span>';
        group.innerHTML =
          `<button type="button" class="lx-filter-group__header" aria-expanded="${!collapsed}" data-filter-toggle>
            <span class="lx-filter-group__title">
              ${config.label}
              <span class="lx-filter-group__badge" data-badge style="display:none">0</span>
              ${loadingBars}
            </span>
            ${SVG.chevron}
          </button>
          <div class="lx-filter-group__content">${listTag}</div>`;

        dom.form.insertBefore(group, availGroup);
      }
    },


    /* ──────────────────────────────────────────────────────────
       FILTER OPTION RENDERING
       ────────────────────────────────────────────────────────── */

    buildFilterOptions() {
      const pool = this.isFullyLoaded ? this.allProducts : this.products;

      // Count resolved values across all products
      const allCounts = {};
      for (const p of pool) {
        for (const [cat, values] of Object.entries(p.attributes)) {
          if (!allCounts[cat]) allCounts[cat] = {};
          for (const val of resolveGroups(cat, values)) {
            allCounts[cat][val] = (allCounts[cat][val] || 0) + 1;
          }
        }
      }

      for (const [category, config] of Object.entries(FILTER_CONFIG)) {
        const container = document.querySelector(`[data-filter-list="${category}"]`);
        if (!container) continue;

        const counts = allCounts[category] || {};
        if (config.type === 'color') {
          this._renderColorSwatches(container, category, config, counts);
        } else {
          this._renderCheckboxes(container, category, config, counts);
        }
      }

      /* Toggle loading → ready state */
      if (this.isFullyLoaded && dom.form) {
        dom.form.classList.add('lx-filters--ready');
      }
    },

    _renderColorSwatches(container, category, config, counts) {
      const order = config.order || Object.keys(config.display);
      const keys = sortByOrderThenCount(
        Object.keys(counts).filter(k => counts[k] > 0 && config.display[k]), order, counts
      );
      const showCounts = this.isFullyLoaded;

      container.innerHTML = keys.map(key => {
        const d = config.display[key] || { label: titleCase(key), hex: '#ccc' };
        const selected = (this.activeFilters[category] || []).includes(key);
        const checkColor = (d.hex === '#f5f5f0' || d.hex === '#ffffff') ? '#333' : '#fff';
        const countStr = showCounts ? ` (${counts[key]})` : '';

        return `<button type="button"
          class="lx-filter-color${selected ? ' is-selected' : ''}"
          data-filter-category="${category}" data-filter-value="${key}" data-color="${key}"
          style="background:${d.hex || '#ccc'}${d.border ? ';box-shadow:inset 0 0 0 1px rgba(0,0,0,.15)' : ''}"
          title="${d.label}${countStr}">
          <span class="lx-filter-color__check"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="${checkColor}" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg></span>
        </button>`;
      }).join('');
    },

    _renderCheckboxes(container, category, config, counts) {
      const source = config.groups || config.display || {};
      const order = config.order || Object.keys(source);
      const keys = sortByOrderThenCount(
        Object.keys(source).filter(k => counts[k] > 0), order, counts
      );
      const showCounts = this.isFullyLoaded;

      container.innerHTML = keys.map(key => {
        const label = source[key]?.label || titleCase(key);
        const checked = (this.activeFilters[category] || []).includes(key);
        const countStr = showCounts ? `(${counts[key]})` : '';

        return `<li class="lx-filter-option"><label>
          <input type="checkbox" data-filter-category="${category}" data-filter-value="${key}" ${checked ? 'checked' : ''}>
          <span class="lx-filter-checkbox">${SVG.check}</span>
          <span class="lx-filter-label">${label}</span>
          <span class="lx-filter-count">${countStr}</span>
        </label></li>`;
      }).join('');
    },


    /* ──────────────────────────────────────────────────────────
       FILTER APPLICATION
       ────────────────────────────────────────────────────────── */

    applyFilters() {
      const hasActive = this.hasActiveFilters();
      const pool = (this.isFullyLoaded && hasActive) ? this.allProducts : this.products;

      const filtered = pool.filter(p =>
        matchesFilters(p, this.activeFilters, this.priceRange, this.inStockOnly, null)
      );

      if (hasActive && this.isFullyLoaded) {
        this.renderFilteredGrid(filtered);
      } else if (this.gridReplaced) {
        this.restoreOriginalGrid();
      } else {
        const ids = new Set(filtered.map(p => p.id));
        for (const p of this.products) {
          if (p.element) p.element.classList.toggle('is-hidden', !ids.has(p.id));
        }
      }

      this.updateUI(filtered.length);
      this.updateFacetedCounts();
    },

    hasActiveFilters() {
      for (const cat in this.activeFilters) {
        if (this.activeFilters[cat].length) return true;
      }
      return this.priceRange.min !== null || this.priceRange.max !== null || this.inStockOnly;
    },

    /**
     * Faceted count update (Amazon/Uniqlo pattern).
     * For each category, counts from a pool filtered by all OTHER active
     * categories. Keeps within-category counts stable after selection.
     */
    updateFacetedCounts() {
      if (!this.isFullyLoaded) return;

      const hasActive = this.hasActiveFilters();

      for (const category of Object.keys(FILTER_CONFIG)) {
        const catPool = hasActive
          ? this.allProducts.filter(p =>
              matchesFilters(p, this.activeFilters, this.priceRange, this.inStockOnly, category)
            )
          : this.allProducts;

        // Count resolved values
        const counts = {};
        for (const p of catPool) {
          for (const val of resolveGroups(category, p.attributes[category] || [])) {
            counts[val] = (counts[val] || 0) + 1;
          }
        }

        // Update checkbox options: count text, grey out zero-count, reorder
        const list = document.querySelector(`[data-filter-list="${category}"]`);
        if (!list) continue;

        const activeVals = new Set(this.activeFilters[category] || []);

        if (FILTER_CONFIG[category].type === 'color') {
          list.querySelectorAll('.lx-filter-color').forEach(sw => {
            const val = sw.dataset.filterValue;
            const count = counts[val] || 0;
            const d = FILTER_CONFIG[category]?.display?.[val];
            if (d) sw.title = d.label + ' (' + count + ')';
            // Don't grey out active selections — user must be able to uncheck
            sw.classList.toggle('is-unavailable', count === 0 && !activeVals.has(val));
          });
          // Push unavailable swatches to end
          const unavail = list.querySelectorAll('.lx-filter-color.is-unavailable');
          unavail.forEach(sw => list.appendChild(sw));
        } else {
          list.querySelectorAll('.lx-filter-option').forEach(li => {
            const input = li.querySelector('input[data-filter-value]');
            const countEl = li.querySelector('.lx-filter-count');
            if (!input) return;
            const val = input.dataset.filterValue;
            const count = counts[val] || 0;
            if (countEl) countEl.textContent = '(' + count + ')';
            li.classList.toggle('is-unavailable', count === 0 && !activeVals.has(val));
          });
          // Push unavailable items to end, preserving relative order
          const unavail = list.querySelectorAll('.lx-filter-option.is-unavailable');
          unavail.forEach(li => list.appendChild(li));
        }
      }
    },


    /* ──────────────────────────────────────────────────────────
       PRODUCT GRID RENDERING
       ────────────────────────────────────────────────────────── */

    renderFilteredGrid(filtered) {
      if (dom.pagination) dom.pagination.style.display = 'none';

      const frag = document.createDocumentFragment();
      const visible = filtered.slice(0, 200);
      for (const p of visible) frag.appendChild(this.createProductCard(p));

      dom.grid.innerHTML = '';
      dom.grid.appendChild(frag);
      this.gridReplaced = true;

      if (filtered.length > 200) {
        const notice = document.createElement('div');
        notice.className = 'lx-filter-notice';
        notice.textContent = `Showing first 200 of ${filtered.length} results. Add more filters to narrow down.`;
        dom.grid.appendChild(notice);
      }
    },

    /** Restore the original server-rendered product cards after clearing filters. */
    restoreOriginalGrid() {
      dom.grid.innerHTML = '';
      const frag = document.createDocumentFragment();
      for (const p of this.products) {
        if (p.element) {
          p.element.classList.remove('is-hidden');
          frag.appendChild(p.element);
        }
      }
      dom.grid.appendChild(frag);
      this.gridReplaced = false;
      if (dom.pagination) dom.pagination.style.display = '';
    },

    createProductCard(product) {
      const card = document.createElement('article');
      card.className = 'lx-product-card';
      card.dataset.productId = product.id;
      card.dataset.productTags = product.tags.join(',');
      card.dataset.productPrice = product.price;
      card.dataset.productAvailable = product.available;

      const discount = product.comparePrice > product.price
        ? Math.round((product.comparePrice - product.price) / product.comparePrice * 100)
        : 0;

      const title = escapeHTML(product.title);
      const showVendor = product.vendor && product.vendor !== 'Luxire' && product.vendor !== 'Luxire Custom Clothing';

      card.innerHTML =
        `<a href="${product.url}" class="lx-product-card__link">
          <div class="lx-product-card__media">
            ${product.image
              ? `<img src="${product.image}" alt="${title}" loading="lazy" width="600">`
                + (product.hoverImage ? `<img src="${product.hoverImage}" alt="${title}" loading="lazy" class="lx-product-card__hover-img">` : '')
              : `<div class="lx-product-card__placeholder">${SVG.image}</div>`}
            ${discount > 0 ? `<span class="lx-product-card__badge lx-product-card__badge--sale">-${discount}%</span>` : ''}
          </div>
          <div class="lx-product-card__info">
            ${showVendor ? `<span class="lx-product-card__vendor">${escapeHTML(product.vendor)}</span>` : ''}
            <h3 class="lx-product-card__title">${title}</h3>
            <div class="lx-product-card__price">
              ${discount > 0
                ? `<span class="lx-product-card__price--sale">${formatMoney(product.price)}</span>`
                  + `<span class="lx-product-card__price--compare">${formatMoney(product.comparePrice)}</span>`
                : `<span>${formatMoney(product.price)}</span>`}
            </div>
            ${!product.available ? '<span class="lx-product-card__sold-out">Sold Out</span>' : ''}
          </div>
        </a>`;

      return card;
    },


    /* ──────────────────────────────────────────────────────────
       UI UPDATES
       ────────────────────────────────────────────────────────── */

    updateUI(visibleCount) {
      dom.visibleCount.textContent = visibleCount;
      dom.resultCount.textContent = visibleCount;
      dom.emptyState.style.display = visibleCount === 0 ? 'block' : 'none';
      dom.grid.style.display = visibleCount === 0 ? 'none' : '';

      let total = 0;
      for (const cat in this.activeFilters) total += this.activeFilters[cat].length;
      if (this.priceRange.min !== null || this.priceRange.max !== null) total++;
      if (this.inStockOnly) total++;

      dom.filterBadge.textContent = total;
      dom.filterBadge.style.display = total > 0 ? '' : 'none';
      dom.toolbarBadge.textContent = total;
      dom.toolbarBadge.style.display = total > 0 ? '' : 'none';

      // Per-category badges
      document.querySelectorAll('.lx-filter-group[data-filter-category]').forEach(group => {
        const badge = group.querySelector('[data-badge]');
        if (!badge) return;
        const count = (this.activeFilters[group.dataset.filterCategory] || []).length;
        badge.textContent = count;
        badge.style.display = count > 0 ? '' : 'none';
      });

      this.renderActiveTags(total);
    },

    renderActiveTags(total) {
      if (total === 0) {
        dom.activeSection.style.display = 'none';
        dom.activeNum.textContent = '0';
        dom.activeTags.innerHTML = '';
        return;
      }

      dom.activeSection.style.display = '';
      dom.activeNum.textContent = total;

      const parts = [];

      for (const cat in this.activeFilters) {
        for (const val of this.activeFilters[cat]) {
          parts.push(
            `<button type="button" class="lx-filter-tag" data-category="${cat}" data-value="${val}">`
            + `${getFilterLabel(cat, val)} ${SVG.close}</button>`
          );
        }
      }

      if (this.priceRange.min !== null || this.priceRange.max !== null) {
        const min = this.priceRange.min ? '$' + (this.priceRange.min / 100) : '$0';
        const max = this.priceRange.max ? '$' + (this.priceRange.max / 100) : '+';
        parts.push(
          `<button type="button" class="lx-filter-tag" data-category="price" data-value="range">`
          + `${min} – ${max} ${SVG.close}</button>`
        );
      }

      if (this.inStockOnly) {
        parts.push(
          `<button type="button" class="lx-filter-tag" data-category="inStock" data-value="true">`
          + `In Stock Only ${SVG.close}</button>`
        );
      }

      dom.activeTags.innerHTML = parts.join('');
    },


    /* ──────────────────────────────────────────────────────────
       URL STATE
       Enables shareable filter links and browser back/forward.
       ────────────────────────────────────────────────────────── */

    saveToURL() {
      const url = new URL(window.location);
      const toDelete = [...url.searchParams.keys()].filter(k =>
        k.startsWith('f.') || k === 'price' || k === 'stock'
      );
      for (const k of toDelete) url.searchParams.delete(k);

      for (const cat in this.activeFilters) {
        if (this.activeFilters[cat].length) {
          url.searchParams.set('f.' + cat, this.activeFilters[cat].join(','));
        }
      }

      if (this.priceRange.min !== null || this.priceRange.max !== null) {
        const min = this.priceRange.min ? this.priceRange.min / 100 : '';
        const max = this.priceRange.max ? this.priceRange.max / 100 : '';
        url.searchParams.set('price', min + '-' + max);
      }

      if (this.inStockOnly) url.searchParams.set('stock', '1');

      history.pushState(null, '', url);
    },

    restoreFromURL() {
      const url = new URL(window.location);

      for (const [key, value] of url.searchParams) {
        if (key.startsWith('f.')) {
          this.activeFilters[key.slice(2)] = value.split(',').filter(Boolean);
        }
      }

      const priceParam = url.searchParams.get('price');
      if (priceParam) {
        const [min, max] = priceParam.split('-');
        if (min) { this.priceRange.min = parseInt(min) * 100; dom.priceMin.value = min; }
        if (max) { this.priceRange.max = parseInt(max) * 100; dom.priceMax.value = max; }
      }

      if (url.searchParams.get('stock') === '1') {
        this.inStockOnly = true;
        dom.inStockToggle.checked = true;
      }
    },

    clearAllFilters() {
      this.activeFilters = {};
      this.priceRange = { min: null, max: null };
      this.inStockOnly = false;

      document.querySelectorAll('input[data-filter-category]').forEach(el => (el.checked = false));
      document.querySelectorAll('.lx-filter-color').forEach(el => el.classList.remove('is-selected'));
      dom.priceMin.value = '';
      dom.priceMax.value = '';
      document.querySelectorAll('[data-price-preset]').forEach(b => b.classList.remove('is-active'));
      dom.inStockToggle.checked = false;

      if (dom.pagination) dom.pagination.style.display = '';
      this.applyFilters();
      this.saveToURL();
    },


    /* ──────────────────────────────────────────────────────────
       EVENT BINDING
       Uses event delegation where possible.
       Split into logical groups for readability.
       ────────────────────────────────────────────────────────── */

    bindEvents() {
      this._bindMobileDrawer();
      this._bindFilterInteractions();
      this._bindPrice();
      this._bindStockToggle();
      this._bindClearButtons();
      this._bindActiveTagRemoval();
      this._bindSortAndView();
      this._bindPopState();
    },

    _bindMobileDrawer() {
      const open  = document.getElementById('filterOpen');
      const close = document.getElementById('filterClose');
      const overlay = document.getElementById('filterOverlay');
      const apply = document.getElementById('applyFilters');

      open?.addEventListener('click', () => {
        dom.sidebar.classList.add('is-open');
        document.body.classList.add('is-scroll-locked');
      });
      close?.addEventListener('click', () => this._closeDrawer());
      overlay?.addEventListener('click', () => this._closeDrawer());
      apply?.addEventListener('click', () => this._closeDrawer());
      document.addEventListener('keydown', e => { if (e.key === 'Escape') this._closeDrawer(); });
    },

    _closeDrawer() {
      if (!dom.sidebar?.classList.contains('is-open')) return;
      dom.sidebar.classList.remove('is-open');
      document.body.classList.remove('is-scroll-locked');
    },

    _bindFilterInteractions() {
      // Accordion toggles
      document.addEventListener('click', e => {
        const toggle = e.target.closest('[data-filter-toggle]');
        if (toggle) {
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', !expanded);
        }
      });

      // Checkbox filters
      document.addEventListener('change', e => {
        if (!e.target.matches('input[data-filter-category]')) return;
        const { filterCategory: cat, filterValue: val } = e.target.dataset;
        if (!this.activeFilters[cat]) this.activeFilters[cat] = [];
        if (e.target.checked) {
          if (!this.activeFilters[cat].includes(val)) this.activeFilters[cat].push(val);
        } else {
          this.activeFilters[cat] = this.activeFilters[cat].filter(v => v !== val);
        }
        this.applyFilters();
        this.saveToURL();
      });

      // Color swatches
      document.addEventListener('click', e => {
        const swatch = e.target.closest('.lx-filter-color');
        if (!swatch) return;
        const { filterCategory: cat, filterValue: val } = swatch.dataset;
        if (!this.activeFilters[cat]) this.activeFilters[cat] = [];
        if (swatch.classList.toggle('is-selected')) {
          this.activeFilters[cat].push(val);
        } else {
          this.activeFilters[cat] = this.activeFilters[cat].filter(v => v !== val);
        }
        this.applyFilters();
        this.saveToURL();
      });
    },

    _bindPrice() {
      let timer;
      const handlePrice = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          this.priceRange.min = dom.priceMin.value ? parseInt(dom.priceMin.value) * 100 : null;
          this.priceRange.max = dom.priceMax.value ? parseInt(dom.priceMax.value) * 100 : null;
          this.applyFilters();
          this.saveToURL();
        }, 400);
      };
      dom.priceMin?.addEventListener('input', handlePrice);
      dom.priceMax?.addEventListener('input', handlePrice);

      document.querySelectorAll('[data-price-preset]').forEach(btn => {
        btn.addEventListener('click', function () {
          const [min, max] = this.dataset.pricePreset.split('-');
          dom.priceMin.value = min || '';
          dom.priceMax.value = max || '';
          document.querySelectorAll('[data-price-preset]').forEach(b => b.classList.remove('is-active'));
          this.classList.add('is-active');
          handlePrice();
        });
      });
    },

    _bindStockToggle() {
      dom.inStockToggle?.addEventListener('change', e => {
        this.inStockOnly = e.target.checked;
        this.applyFilters();
        this.saveToURL();
      });
    },

    _bindClearButtons() {
      document.getElementById('clearAllFilters')?.addEventListener('click', () => this.clearAllFilters());
      document.getElementById('emptyStateClear')?.addEventListener('click', () => this.clearAllFilters());
    },

    _bindActiveTagRemoval() {
      dom.activeTags?.addEventListener('click', e => {
        const tag = e.target.closest('.lx-filter-tag');
        if (!tag) return;
        const { category: cat, value: val } = tag.dataset;

        if (cat === 'price') {
          dom.priceMin.value = '';
          dom.priceMax.value = '';
          this.priceRange = { min: null, max: null };
          document.querySelectorAll('[data-price-preset]').forEach(b => b.classList.remove('is-active'));
        } else if (cat === 'inStock') {
          dom.inStockToggle.checked = false;
          this.inStockOnly = false;
        } else {
          this.activeFilters[cat] = (this.activeFilters[cat] || []).filter(v => v !== val);
          const cb = document.querySelector(`input[data-filter-category="${cat}"][data-filter-value="${val}"]`);
          if (cb) cb.checked = false;
          const sw = document.querySelector(`.lx-filter-color[data-filter-category="${cat}"][data-filter-value="${val}"]`);
          if (sw) sw.classList.remove('is-selected');
        }

        this.applyFilters();
        this.saveToURL();
      });
    },

    _bindSortAndView() {
      dom.sortSelect?.addEventListener('change', e => {
        const u = new URL(window.location);
        u.searchParams.set('sort_by', e.target.value);
        window.location = u;
      });

      document.querySelectorAll('[data-view]').forEach(btn => {
        btn.addEventListener('click', function () {
          document.querySelectorAll('[data-view]').forEach(b => b.classList.remove('is-active'));
          this.classList.add('is-active');
          dom.grid.classList.toggle('lx-product-grid--list', this.dataset.view === 'list');
          if (typeof LuxireStorage !== 'undefined') LuxireStorage.saveViewPreference(this.dataset.view);
        });
      });

      try {
        const saved = (typeof LuxireStorage !== 'undefined') ? LuxireStorage.getViewPreference() : null;
        if (saved) document.querySelector(`[data-view="${saved}"]`)?.click();
      } catch (_) {}
    },

    /** Restore filter state when user navigates with browser back/forward. */
    _bindPopState() {
      window.addEventListener('popstate', () => {
        // Reset all state
        this.activeFilters = {};
        this.priceRange = { min: null, max: null };
        this.inStockOnly = false;

        // Clear all UI controls
        document.querySelectorAll('input[data-filter-category]').forEach(el => (el.checked = false));
        document.querySelectorAll('.lx-filter-color').forEach(el => el.classList.remove('is-selected'));
        dom.priceMin.value = '';
        dom.priceMax.value = '';
        document.querySelectorAll('[data-price-preset]').forEach(b => b.classList.remove('is-active'));
        dom.inStockToggle.checked = false;

        // Restore from current URL and re-apply
        this.restoreFromURL();
        this.buildFilterOptions();
        this.applyFilters();
      });
    }
  };


  /* ── Boot ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => Engine.init());
  } else {
    Engine.init();
  }

})();
